import type { ComponentProps, ReactNode } from 'react';
import React from 'react';

import type { IconName } from '../../assets/Icon/Icon';
import type { VariantProp } from '../../common/colors';
import { Icon } from '../../assets/Icon/Icon';
import { Tooltip } from '../../components/Tooltip/Tooltip';
import { selectors, transitions } from '../../controls/shared/styles';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { Caption } from '../../text/Caption';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { Port } from '../Port/Port';
import {
  type PortSpeed,
  type PortStatus,
  getPortIcon,
  getPortSpeedLabel,
  getPortStatusLabel,
} from '../Port/utils';

const legendDividerWidth = '$2';

const LegendIcon = styled(Icon, {
  color: '$$legendIconColor',
});
LegendIcon.displayName = 'LegendIcon';

const LegendLabel = styled(Text, {
  color: '$$legendLabelColor',
  whiteSpace: 'nowrap',
});
LegendLabel.displayName = 'LegendLabel';

const LegendContainer = styled(AlignStack, {
  position: 'relative',
  zIndex: 2,
  width: '$$legendWidth',
  minWidth: 0,
  padding: '$2 $6',
  borderRadius: '$6',
  cursor: 'pointer',
  transition: transitions.focusRing,
  $$legendLabelColor: colors.bodyNeutralLight,
  $$legendIconColor: colors.iconNeutralLight,

  [darkThemeSelector]: {
    $$legendLabelColor: colors.bodyNeutralDark,
    $$legendIconColor: colors.iconNeutralDark,
  },

  variants: {
    direction: {
      column: {},
      row: {
        '&::after': {
          content: '',
          position: 'absolute',
          top: '$3',
          right: '-$4',
          bottom: '$3',
          transform: 'translateX(50%)',
          width: legendDividerWidth,
          backgroundColor: colors.strokeNeutralLight,
          borderRadius: '99em',

          [darkThemeSelector]: {
            backgroundColor: colors.strokeNeutralDark,
          },
        },

        '&:last-child::after': {
          display: 'none',
        },
      },
    },
    isPressable: {
      true: {
        [selectors.hover]: {
          $$legendLabelColor: colors.headingNeutralLight,
          $$legendIconColor: colors.bodyNeutralLight,

          [darkThemeSelector]: {
            $$legendLabelColor: colors.headingNeutralDark,
            $$legendIconColor: colors.bodyNeutralDark,
          },
        },

        [selectors.focus]: {
          boxShadow: shadows.focusRingLight,
          outline: 'none',

          [darkThemeSelector]: {
            boxShadow: shadows.focusRingDark,
          },
        },
      },
      false: {},
    },
    selected: {
      true: {
        [selectors.all]: {
          backgroundColor: colors.bgBrandLight,
          strokeAll: colors.strokeBrandLight,
          $$legendLabelColor: colors.headingBrandLight,
          $$legendIconColor: colors.bodyBrandLight,

          [darkThemeSelector]: {
            backgroundColor: colors.bgBrandDark,
            strokeAll: colors.strokeBrandDark,
            $$legendLabelColor: colors.headingBrandDark,
            $$legendIconColor: colors.bodyBrandDark,
          },
        },
      },
      false: {},
    },
  },
});
LegendContainer.displayName = 'LegendContainer';

export type LegendProps = {
  blocking?: boolean;
  collapsed?: boolean;
  direction?: 'column' | 'row';
  error?: boolean;
  icon?: IconName;
  label?: React.ReactNode;
  onClick?: (event: any) => void;
  poe?: boolean;
  selected?: boolean;
  speed?: PortSpeed;
  status?: PortStatus;
  uplink?: boolean;
  /**
   * The colour of the `icon`, for an entry whose mark on the faceplate is coloured — the bolt of a
   * port drawing power. Left off, the icon takes the row's own colour and follows it through hover
   * and selection.
   */
  variant?: VariantProp;
};

export function Legend({
  blocking,
  collapsed,
  direction,
  error,
  icon,
  label,
  onClick,
  poe,
  selected,
  speed,
  status,
  uplink,
  variant,
  ...remaining
}: LegendProps) {
  const portIcon = status ? getPortIcon(uplink, blocking, poe) : icon;
  const displayLabel =
    label ||
    (speed && status ? `${getPortSpeedLabel(speed)} ${getPortStatusLabel(status)}` : undefined);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (onClick && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onClick(event);
    }
  };

  const portLegendRender = (
    <LegendContainer
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={onClick && 'button'}
      tabIndex={onClick ? 0 : undefined}
      isPressable={!!onClick}
      direction={direction}
      align="center"
      gap={6}
      preset="small"
      selected={selected}
      start={
        portIcon ? (
          // A variant here outranks `$$legendIconColor`, so a coloured entry holds its colour
          // through hover and selection. That is the same deal the speed entries get from drawing a
          // real `Port`: a glossary is only worth reading if its swatch is the thing it explains.
          <LegendIcon icon={portIcon} size={14} variant={variant} />
        ) : (
          <Port
            port="ethernet"
            negotiatedPortSpeed={speed}
            status={status}
            uplink={uplink}
            blocking={blocking}
            mode="icon"
          />
        )
      }
      {...remaining}
    >
      {!collapsed && <LegendLabel>{displayLabel}</LegendLabel>}
    </LegendContainer>
  );

  if (collapsed) {
    return (
      <Tooltip align="center" side="left" sideOffset={6} toggle={false} contents={displayLabel}>
        {portLegendRender}
      </Tooltip>
    );
  }

  return portLegendRender;
}

const LegendsContainer = styled('div', {
  display: 'flex',

  variants: {
    direction: {
      column: {
        flexDirection: 'column',
      },
      row: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: '$8',
      },
    },
  },
});
LegendsContainer.displayName = 'LegendsContainer';

// A group heading is muted, not disabled — `disabledNeutral` is deliberately below AA.
const LegendsGroupLabel = styled(Caption, {
  color: colors.accentNeutralLight,

  [darkThemeSelector]: {
    color: colors.accentNeutralDark,
  },
});
LegendsGroupLabel.displayName = 'LegendsGroupLabel';

const LegendsGroupsGroup = styled(AlignStack, {
  display: 'flex',
  minWidth: 'auto',
});
LegendsGroupsGroup.displayName = 'LegendsGroupsGroup';

// AlignStack centres its slots, which suits a row of legends but centres a column's caption and
// rows; a plain stack keeps them flush with the rail's edge.
const LegendsGroupsColumnGroup = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$4',
});
LegendsGroupsColumnGroup.displayName = 'LegendsGroupsColumnGroup';

// Indented to sit on the same margin as the rows' icons.
const LegendsGroupsColumnLabel = styled(LegendsGroupLabel, {
  padding: '0 $6',
});
LegendsGroupsColumnLabel.displayName = 'LegendsGroupsColumnLabel';

const LegendsGroupsContainer = styled('div', {
  display: 'flex',
  gap: '$12',

  variants: {
    direction: {
      column: {
        flexDirection: 'column',
        $$legendWidth: '100%',
      },
      row: {
        flexDirection: 'row',
      },
    },
  },
});
LegendsGroupsContainer.displayName = 'LegendsGroupsContainer';

export type LegendsGroupProps = {
  group: ReactNode;
  legends: LegendProps[];
};

export type LegendsProps = {
  /**
   * Allow for collapsed legends.
   */
  collapsed?: boolean;
  /**
   * Allow for the direction of the legends.
   */
  direction?: 'row' | 'column';
  /**
   * Allow for grouped legends.
   */
  grouped?: LegendsGroupProps[];
  /**
   * Allow for regular legends.
   */
  legends?: LegendProps[];
} & ComponentProps<typeof LegendsContainer>;

export function Legends({
  collapsed,
  direction = 'row',
  grouped,
  legends,
  ...remaining
}: LegendsProps) {
  if (grouped && grouped.length > 0) {
    return (
      <LegendsGroupsContainer direction={direction} {...remaining}>
        {grouped.map((group) => {
          const rows = (
            <LegendsContainer direction={direction}>
              {group.legends.map((legend) => (
                <Legend
                  key={`legend-${group.group}-${legend.label}`}
                  {...legend}
                  collapsed={collapsed}
                  direction={direction}
                />
              ))}
            </LegendsContainer>
          );

          if (direction === 'column') {
            return (
              <LegendsGroupsColumnGroup key={`group-${group.group}`}>
                {!collapsed && <LegendsGroupsColumnLabel>{group.group}</LegendsGroupsColumnLabel>}
                {rows}
              </LegendsGroupsColumnGroup>
            );
          }

          return (
            <LegendsGroupsGroup
              key={`group-${group.group}`}
              align="center"
              direction={direction}
              gap={4}
              preset="small"
              width="fit-content"
              start={<LegendsGroupLabel>{group.group}</LegendsGroupLabel>}
            >
              {rows}
            </LegendsGroupsGroup>
          );
        })}
      </LegendsGroupsContainer>
    );
  }

  return (
    <LegendsContainer direction={direction} {...remaining}>
      {legends?.map((legend) => (
        <Legend
          key={`legend-${legend.label}`}
          {...legend}
          collapsed={collapsed}
          direction={direction}
        />
      ))}
    </LegendsContainer>
  );
}
